import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, TextInput, Textarea, Select, Modal, Badge } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { useTranslation } from '../context/TranslationContext';
import LanguageTabs from '../components/LanguageTabs';
import apiService from '../services/api';
import './AdminEmailTemplates.css';

function formatDateTime(str) {
  if (!str) return '—';
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-CH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// The admin types plain text; the HTML is generated for storage.
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Convert stored HTML back to editable plain text (tags stripped, breaks kept).
function htmlToText(html) {
  if (!html) return '';
  let text = String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
    .replace(/<(p|div|li|h[1-6]|tr)[^>]*>/gi, '\n')
    .replace(/<[^>]*>/g, '');
  text = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'");
  return text.replace(/\n{3,}/g, '\n\n').trim();
}

// Convert editable plain text into the HTML stored in the database. Lines are
// processed one at a time: a line that is exactly a {{block:...}} token is kept
// raw (it renders block-level HTML); all other lines are escaped and grouped
// into <p> paragraphs (blank lines separate paragraphs, single newlines become
// <br>).
function textToHtml(text) {
  const out = [];
  let para = [];
  const flush = () => {
    if (para.length) {
      out.push(`<p>${para.join('<br>')}</p>`);
      para = [];
    }
  };
  for (const line of String(text || '').split('\n')) {
    const trimmed = line.trim();
    if (/^\{\{block:[^{}]+\}\}$/.test(trimmed)) {
      flush();
      out.push(trimmed);
    } else if (!trimmed) {
      flush();
    } else {
      para.push(escapeHtml(trimmed));
    }
  }
  flush();
  return out.join('\n');
}

export default function AdminEmailTemplates() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [workflowTokens, setWorkflowTokens] = useState({});
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Editor modal state
  const [modal, setModal] = useState({ open: false, mode: 'create', intent: 'global', template: null, langTab: undefined });
  const [form, setForm] = useState({ userId: '', langs: {} });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const bodyRefs = useRef({});

  const fetchAll = useCallback(async () => {
    try {
      const [data, langs] = await Promise.all([
        apiService.getEmailTemplates(),
        apiService.getLanguages(),
      ]);
      setEvents(data.events || []);
      setWorkflowTokens(data.workflowTokens || {});
      setTemplates(data.templates || []);
      setLanguages(Array.isArray(langs) ? langs : []);
      setSelectedEvent(prev => prev || (data.events || [])[0] || '');
    } catch (err) {
      console.error(err);
      notifications.show({ title: 'Error', message: err.message || 'Failed to load email templates', color: 'red' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const loadUsers = useCallback(async () => {
    try {
      setUsers(await apiService.getUsers());
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // ── Derived data for the selected workflow ─────────────────────────────
  const globalRows = templates.filter(tp => tp.event_key === selectedEvent && !tp.user_id);
  const userRows = templates.filter(tp => tp.event_key === selectedEvent && tp.user_id);

  const languageRows = languages.length > 0
    ? languages.map(lang => {
        const row = globalRows.find(r => r.language_code === lang.code);
        return { lang, row };
      })
    : [];

  // ── Mutations ────────────────────────────────────────────────────────────
  const reloadTemplates = useCallback(async () => {
    const data = await apiService.getEmailTemplates();
    setTemplates(data.templates || []);
  }, []);

  const emptyLangEntry = () => ({ subject: '', body: '' });

  const buildLangs = (rows, fallbackRows = []) => {
    const map = {};
    languages.forEach(l => {
      const existing = rows.find(r => r.language_code === l.code);
      const fallback = fallbackRows.find(r => r.language_code === l.code);
      map[l.code] = existing
        ? { subject: existing.subject || '', body: htmlToText(existing.body_html) }
        : fallback
          ? { subject: fallback.subject || '', body: htmlToText(fallback.body_html) }
          : emptyLangEntry();
    });
    return map;
  };

  const setLang = (langCode, patch) => {
    setForm(prev => ({
      ...prev,
      langs: {
        ...prev.langs,
        [langCode]: { ...(prev.langs[langCode] || emptyLangEntry()), ...patch },
      },
    }));
  };

  const openEditor = ({ mode, template = null, intent = 'global', languageCode }) => {
    setFormError('');
    if (mode === 'edit' && template) {
      const siblings = templates.filter(t => t.event_key === selectedEvent && (t.user_id || null) === (template.user_id || null));
      const isUserScope = !!template.user_id;
      setForm({
        userId: template.user_id || '',
        langs: buildLangs(siblings, isUserScope ? globalRows : []),
      });
      setModal({ open: true, mode, template, intent: template.user_id ? 'user' : 'global', langTab: template.language_code });
    } else {
      setForm({ userId: '', langs: buildLangs([], intent === 'user' ? globalRows : []) });
      setModal({ open: true, mode, template: null, intent, langTab: languageCode || undefined });
    }
  };

  const closeEditor = () => setModal(prev => ({ ...prev, open: false }));

  const insertToken = (token, langCode) => {
    const ta = bodyRefs.current[langCode];
    const current = form.langs[langCode]?.body || '';
    const start = ta?.selectionStart ?? current.length;
    const end = ta?.selectionEnd ?? current.length;
    const next = current.slice(0, start) + token + current.slice(end);
    setLang(langCode, { body: next });
    if (ta) {
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + token.length;
        try { ta.setSelectionRange(pos, pos); } catch (_) { /* noop */ }
      });
    }
  };

  const saveTemplate = async () => {
    if (modal.intent === 'user' && !form.userId) {
      setFormError(t('emailTemplatesValidationUser', 'Select a user.'));
      return;
    }
    const entries = Object.entries(form.langs || {})
      .filter(([, e]) => (e.subject || '').trim() || (e.body || '').trim())
      .map(([languageCode, e]) => ({
        languageCode,
        subject: String(e.subject || '').trim(),
        bodyHtml: textToHtml(e.body),
      }));
    if (entries.length === 0 || entries.some(e => !e.subject)) {
      setFormError(t('emailTemplatesValidationSubject', 'A subject is required.'));
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const userId = form.userId || null;
      const existingRows = templates.filter(t => t.event_key === selectedEvent && (t.user_id || null) === (userId || null));
      await Promise.all(entries.map(async ({ languageCode, subject, bodyHtml }) => {
        const existing = existingRows.find(r => r.language_code === languageCode);
        if (modal.mode === 'edit' && existing) {
          await apiService.updateEmailTemplate(existing.id, { subject, bodyHtml });
        } else {
          await apiService.createEmailTemplate({ eventKey: selectedEvent, userId, languageCode, subject, bodyHtml });
        }
      }));
      await reloadTemplates();
      closeEditor();
      notifications.show({
        title: t('emailTemplatesSavedTitle', 'Template saved'),
        message: t('emailTemplatesSavedNotif', 'The template is now in use.'),
        color: 'green',
      });
    } catch (err) {
      setFormError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    const langCode = modal.langTab || (languages.length > 0 ? languages[0].code : '');
    const entry = (form.langs && form.langs[langCode]) || emptyLangEntry();
    if (!(entry.subject || '').trim()) {
      setFormError(t('emailTemplatesValidationSubject', 'A subject is required.'));
      return;
    }
    setTesting(true);
    setFormError('');
    try {
      const res = await apiService.sendTestEmail({
        eventKey: selectedEvent,
        languageCode: langCode,
        subject: String(entry.subject || '').trim(),
        bodyHtml: textToHtml(entry.body || ''),
      });
      notifications.show({
        title: t('emailTemplatesTestSentTitle', 'Test email sent'),
        message: `${t('emailTemplatesTestSentNotif', 'Test email sent to')} ${res.to}`,
        color: 'green',
      });
    } catch (err) {
      setFormError(err.message || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  const deleteTemplate = (id) => {
    modals.openConfirmModal({
      title: t('emailTemplatesDelete', 'Delete'),
      centered: true,
      children: (
        <p className="email-tpl-delete-text">
          {t('emailTemplatesConfirmDelete', 'Delete this template?')}
        </p>
      ),
      labels: { confirm: t('emailTemplatesDelete', 'Delete'), cancel: t('cancel', 'Cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setBusyId(id);
        try {
          await apiService.deleteEmailTemplate(id);
          await reloadTemplates();
          notifications.show({ title: t('emailTemplatesDeletedTitle', 'Template deleted'), message: '', color: 'green' });
        } catch (err) {
          notifications.show({ title: 'Error', message: err.message, color: 'red' });
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const renderActions = (row, langCode = '') => {
    const buttons = [];
    if (row) {
      buttons.push(
        <Button key="edit" size="xs" variant="light" onClick={() => openEditor({ mode: 'edit', template: row })}>
          {t('emailTemplatesEdit', 'Edit')}
        </Button>
      );
      buttons.push(
        <Button key="delete" size="xs" variant="subtle" color="red" loading={busyId === row.id} onClick={() => deleteTemplate(row.id)}>
          {t('emailTemplatesDelete', 'Delete')}
        </Button>
      );
    } else {
      buttons.push(
        <Button key="match" size="xs" color="blue" onClick={() => openEditor({ mode: 'create', intent: 'global', languageCode: langCode })}>
          {t('emailTemplatesMatch', 'Match')}
        </Button>
      );
    }
    return <div className="email-tpl-actions">{buttons}</div>;
  };

  const userLabel = (u) => (u.name ? `${u.name} (${u.email})` : u.email);

  if (loading) {
    return (
      <div className="admin-products dashboard-view-wrapper">
        <p role="status" aria-live="polite">{t('loading', 'Loading…')}</p>
      </div>
    );
  }

  const eventKeys = events;

  return (
    <div className="admin-products dashboard-view-wrapper">
      <header className="dashboard-page-header">
        <div>
          <h1 className="dashboard-page-title">{t('emailTemplates', 'Email Templates')}</h1>
          <p className="dashboard-page-description">
            {t('emailTemplatesDesc', 'Manage the email templates used for transactional emails. Every workflow is matched per language, with optional per-user overrides.')}
          </p>
        </div>
      </header>

      <div className="email-tpl-layout">
        {/* Workflow list */}
        <nav className="email-tpl-workflow-list" aria-label={t('emailTemplatesWorkflowNav', 'Email workflows')}>
          <h2 className="email-tpl-section-title">{t('emailTemplatesWorkflow', 'Workflow')}</h2>
          {eventKeys.map(key => (
            <button
              key={key}
              type="button"
              className={`email-tpl-workflow-item ${selectedEvent === key ? 'active' : ''}`}
              onClick={() => setSelectedEvent(key)}
              aria-pressed={selectedEvent === key}
            >
              <span className="email-tpl-workflow-key">{key.replace(/_/g, ' ')}</span>
            </button>
          ))}
        </nav>

        {/* Workflow detail */}
        <div className="email-tpl-main">
          {selectedEvent && (
            <>
              {/* Global templates */}
              <section className="email-tpl-section" aria-labelledby="email-tpl-global-title">
                <div className="email-tpl-section-header">
                  <div>
                    <h3 id="email-tpl-global-title" className="email-tpl-section-title">
                      {t('emailTemplatesGlobal', 'Global templates')}
                    </h3>
                    <p className="email-tpl-section-desc">
                      {t('emailTemplatesGlobalDesc', 'Fallback templates used for every customer, matched by language.')}
                    </p>
                  </div>
                  <Button size="xs" onClick={() => openEditor({ mode: 'create', intent: 'global' })}>
                    {t('emailTemplatesAddGlobal', 'Add global template')}
                  </Button>
                </div>
                <div className="table-responsive">
                  <table className="admin-table email-tpl-table">
                    <caption className="sr-only">
                      {t('emailTemplatesGlobalCaption', 'Global templates per language for this workflow, with subject and actions')}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('emailTemplatesLanguage', 'Language')}</th>
                        <th scope="col">{t('emailTemplatesSubject', 'Subject')}</th>
                        <th scope="col">{t('emailTemplatesUpdated', 'Updated')}</th>
                        <th scope="col">{t('emailTemplatesActions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {languageRows.map(({ lang, row }) => (
                        <tr key={lang.code}>
                          <td><span className="email-tpl-lang">{lang.native_name || lang.name}</span></td>
                          <td className="email-tpl-subject-cell">{row?.subject || '—'}</td>
                          <td>{row ? formatDateTime(row.updated_at) : '—'}</td>
                          <td>{renderActions(row, lang.code)}</td>
                        </tr>
                      ))}
                      {languageRows.length === 0 && (
                        <tr>
                          <td colSpan="4">{t('emailTemplatesNoLanguages', 'No languages are configured yet.')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* User-specific overrides */}
              {/* User-specific overrides */}
              <section className="email-tpl-section" aria-labelledby="email-tpl-user-title">
                <div className="email-tpl-section-header">
                  <div>
                    <h3 id="email-tpl-user-title" className="email-tpl-section-title">
                      {t('emailTemplatesUserOverrides', 'User-specific overrides')}
                    </h3>
                    <p className="email-tpl-section-desc">
                      {t('emailTemplatesUserOverridesDesc', 'Templates that win for a specific customer. Matched before the global template.')}
                    </p>
                  </div>
                  <Button size="xs" color="violet" onClick={() => openEditor({ mode: 'create', intent: 'user' })}>
                    {t('emailTemplatesAddUser', 'Add user-specific template')}
                  </Button>
                </div>
                <div className="table-responsive">
                  <table className="admin-table email-tpl-table">
                    <caption className="sr-only">
                      {t('emailTemplatesUserCaption', 'User-specific overrides for this workflow, with user, language, subject and actions')}
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">{t('emailTemplatesUser', 'User')}</th>
                        <th scope="col">{t('emailTemplatesLanguage', 'Language')}</th>
                        <th scope="col">{t('emailTemplatesSubject', 'Subject')}</th>
                        <th scope="col">{t('emailTemplatesUpdated', 'Updated')}</th>
                        <th scope="col">{t('emailTemplatesActions', 'Actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userRows.map(row => (
                        <tr key={row.id}>
                          <td>{row.user_name || row.user_id}</td>
                          <td><span className="email-tpl-lang">{row.language_code}</span></td>
                          <td className="email-tpl-subject-cell">{row.subject || '—'}</td>
                          <td>{formatDateTime(row.updated_at)}</td>
                          <td>{renderActions(row)}</td>
                        </tr>
                      ))}
                      {userRows.length === 0 && (
                        <tr>
                          <td colSpan="5">{t('emailTemplatesNoOverrides', 'No user-specific overrides yet.')}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      {/* Editor modal */}
      <Modal
        opened={modal.open}
        onClose={closeEditor}
        title={modal.mode === 'edit'
          ? t('emailTemplatesEditTitle', 'Edit template')
          : t('emailTemplatesNewTitle', 'New template')}
        size="lg"
        centered
      >
        <div className="email-tpl-editor">
          <p className="email-tpl-editor-scope">
            <span className="email-tpl-editor-scope-item">
              {t('emailTemplatesWorkflowLabel', 'Workflow')}: <Badge size="sm">{selectedEvent.replace(/_/g, ' ')}</Badge>
            </span>
            <span className="email-tpl-editor-scope-sep" aria-hidden="true">·</span>
            <span className="email-tpl-editor-scope-item">
              {t('emailTemplatesEditorScope', 'Scope')}:{' '}
              <Badge color={form.userId || modal.intent === 'user' ? 'violet' : 'blue'} size="sm">
                {form.userId || modal.intent === 'user'
                  ? t('emailTemplatesUserBadge', 'User override')
                  : t('emailTemplatesGlobalBadge', 'Global')}
              </Badge>
            </span>
          </p>
          {modal.intent === 'user' && (
            <Select
              label={t('emailTemplatesUser', 'User')}
              placeholder={t('emailTemplatesSelectUser', 'Select a user…')}
              data={users.map(u => ({ value: u.id, label: userLabel(u) }))}
              value={form.userId}
              onChange={(v) => {
                const nextUserId = v || '';
                const userRows = templates.filter(t => t.event_key === selectedEvent && t.user_id === nextUserId);
                setForm(prev => ({ ...prev, userId: nextUserId, langs: buildLangs(userRows, globalRows) }));
              }}
              disabled={modal.mode === 'edit'}
              searchable
              nothingFoundMessage={t('emailTemplatesNoUsers', 'No users found')}
              withAsterisk
            />
          )}
          <LanguageTabs value={modal.langTab} onChange={(tab) => setModal(prev => ({ ...prev, langTab: tab }))}>
            {({ langCode }) => {
              const langEntry = form.langs[langCode] || emptyLangEntry();
              const bodyId = `email-tpl-body-${langCode}`;
              return (
                <div className="email-tpl-lang-editor" key={langCode}>
                  <TextInput
                    label={t('emailTemplatesSubject', 'Subject')}
                    value={langEntry.subject}
                    onChange={(e) => setLang(langCode, { subject: e.currentTarget.value })}
                    placeholder={t('emailTemplatesSubjectPlaceholder', 'Subject line — supports {{param}} tokens')}
                    withAsterisk
                  />
                  <div className="email-tpl-editor-body-wrap">
                    <label className="email-tpl-editor-body-label" htmlFor={bodyId}>
                      {t('emailTemplatesBody', 'Body')}
                    </label>
                    <Textarea
                      id={bodyId}
                      ref={(node) => { bodyRefs.current[langCode] = node; }}
                      className="email-tpl-textarea"
                      value={langEntry.body}
                      onChange={(e) => setLang(langCode, { body: e.currentTarget.value })}
                      placeholder={t('emailTemplatesBodyPlaceholder', 'Hello {{userName}},\n\nThank you for your order.\n\n{{block:itemsList}}')}
                      minRows={12}
                      autosize
                    />
                    {(() => {
                      const eventTokens = workflowTokens[selectedEvent];
                      const tokenParams = eventTokens?.params || [];
                      const tokenBlocks = eventTokens?.blocks || [];
                      if (tokenParams.length === 0 && tokenBlocks.length === 0) return null;
                      return (
                        <div className="email-tpl-tokens">
                          <span className="email-tpl-tokens-label">{t('emailTemplatesTokens', 'Insert tokens')}:</span>
                          {tokenParams.map(p => (
                            <button key={`p-${p}`} type="button" className="email-tpl-token-chip" onClick={() => insertToken(`{{${p}}}`, langCode)}>
                              {'{{'}{p}{'}}'}
                            </button>
                          ))}
                          {tokenBlocks.map(b => (
                            <button key={`b-${b}`} type="button" className="email-tpl-token-chip" onClick={() => insertToken(`{{block:${b}}}`, langCode)}>
                              {'{{block:'}{b}{'}}'}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                    <p className="email-tpl-body-hint">
                      {t('emailTemplatesBodyHint', 'Plain text — the HTML is generated automatically. {{param}} tokens are auto-escaped; {{block:name}} tokens render pre-built blocks (items, totals, addresses).')}
                    </p>
                  </div>
                </div>
              );
            }}
          </LanguageTabs>
          {formError && (
            <p className="email-tpl-form-error" role="alert">{formError}</p>
          )}
          <div className="email-tpl-editor-actions">
            <Button variant="subtle" color="gray" onClick={closeEditor} disabled={saving || testing}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button variant="light" color="teal" onClick={sendTest} loading={testing} disabled={saving}>
              {t('emailTemplatesTestSend', 'Send test email')}
            </Button>
            <Button onClick={saveTemplate} loading={saving}>
              {modal.mode === 'edit' ? t('emailTemplatesSave', 'Save changes') : t('emailTemplatesSave', 'Save template')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
